/**
 * Cálculo de puntuaciones para la Encuesta de Clima Organizacional.
 */

import { CLIMATE_QUESTIONS } from '@/config/climateQuestions';
import type { ClimateDimension } from '@/config/climateQuestions';

export const LIKERT_DIMENSIONS: ClimateDimension[] = [
  'leadership',
  'communication',
  'growth',
  'wellbeing',
  'belonging',
];

export interface ClimateResponse {
  assessmentId: string;
  department?: string;
  answers: Record<string, number | string>;
}

/** Convierte escala Likert 1-5 a 0-100. */
function likertTo100(value: number): number {
  if (typeof value !== 'number' || value < 1 || value > 5) return NaN;
  return ((value - 1) / 4) * 100;
}

/** Obtiene las preguntas Likert por dimensión. */
function getLikertByDimension(): Map<ClimateDimension, string[]> {
  const map = new Map<ClimateDimension, string[]>();
  for (const q of CLIMATE_QUESTIONS) {
    if (q.type === 'likert' && LIKERT_DIMENSIONS.includes(q.dimension)) {
      const arr = map.get(q.dimension) ?? [];
      arr.push(q.id);
      map.set(q.dimension, arr);
    }
  }
  return map;
}

const LIKERT_BY_DIM = getLikertByDimension();

/**
 * Promedio general de todas las preguntas tipo Likert (escala 1-5 transformada a 0-100).
 */
export function calculateGlobalScore(responses: ClimateResponse[]): number {
  let sum = 0;
  let count = 0;
  for (const r of responses) {
    for (const q of CLIMATE_QUESTIONS) {
      if (q.type === 'likert') {
        const v = r.answers[q.id];
        if (typeof v === 'number') {
          const scaled = likertTo100(v);
          if (!Number.isNaN(scaled)) {
            sum += scaled;
            count++;
          }
        }
      }
    }
  }
  return count === 0 ? 0 : Math.round(sum / count);
}

/**
 * eNPS: %Promotores (9-10) - %Detractores (0-6).
 * Neutros: 7-8.
 * Resultado entre -100 y +100.
 */
export function calculateENPS(responses: ClimateResponse[]): {
  score: number;
  promoters: number;
  neutrals: number;
  detractors: number;
  total: number;
  label: string;
} {
  const enpsValues: number[] = [];
  for (const r of responses) {
    const v = r.answers.enps;
    if (typeof v === 'number' && v >= 0 && v <= 10) {
      enpsValues.push(v);
    }
  }
  const total = enpsValues.length;
  if (total === 0) {
    return { score: 0, promoters: 0, neutrals: 0, detractors: 0, total: 0, label: 'Sin datos' };
  }
  const promoters = enpsValues.filter((v) => v >= 9).length;
  const neutrals = enpsValues.filter((v) => v >= 7 && v <= 8).length;
  const detractors = enpsValues.filter((v) => v <= 6).length;
  const score = Math.round((promoters / total) * 100 - (detractors / total) * 100);
  const label =
    score >= 50 ? 'Excelente' : score >= 20 ? 'Bueno' : score >= 0 ? 'Aceptable' : score >= -50 ? 'Mejorable' : 'Crítico';
  return { score, promoters, neutrals, detractors, total, label };
}

export interface DimensionScore {
  dimension: ClimateDimension;
  score: number;
  label: string;
}

const DIMENSION_LABELS: Record<ClimateDimension, string> = {
  leadership: 'Liderazgo',
  communication: 'Comunicación',
  growth: 'Crecimiento',
  wellbeing: 'Bienestar',
  belonging: 'Pertenencia',
  loyalty: 'Lealtad',
  general: 'General',
};

/**
 * Devuelve un array con { dimension, score } para cada una de las 5 dimensiones Likert.
 */
export function calculateDimensionScores(responses: ClimateResponse[]): DimensionScore[] {
  const result: DimensionScore[] = [];
  for (const dim of LIKERT_DIMENSIONS) {
    const questionIds = LIKERT_BY_DIM.get(dim) ?? [];
    let sum = 0;
    let count = 0;
    for (const r of responses) {
      for (const qid of questionIds) {
        const v = r.answers[qid];
        if (typeof v === 'number') {
          const scaled = likertTo100(v);
          if (!Number.isNaN(scaled)) {
            sum += scaled;
            count++;
          }
        }
      }
    }
    const score = count === 0 ? 0 : Math.round(sum / count);
    result.push({
      dimension: dim,
      score,
      label: DIMENSION_LABELS[dim],
    });
  }
  return result;
}

export interface HeatmapCell {
  department: string;
  dimension: ClimateDimension;
  score: number;
}

export interface HeatmapRow {
  department: string;
  scores: Record<ClimateDimension, number>;
  average: number;
}

/**
 * Agrupa por departamento y calcula el score de cada dimensión para ese departamento.
 */
export function calculateHeatmap(responses: ClimateResponse[]): HeatmapRow[] {
  const byDept = new Map<string, ClimateResponse[]>();
  for (const r of responses) {
    const key = r.department?.trim() || 'Sin especificar';
    const arr = byDept.get(key) ?? [];
    arr.push(r);
    byDept.set(key, arr);
  }

  const rows: HeatmapRow[] = [];
  for (const [dept, deptResponses] of byDept) {
    const dimScores = calculateDimensionScores(deptResponses);
    const scores: Record<string, number> = {};
    let sum = 0;
    let count = 0;
    for (const ds of dimScores) {
      scores[ds.dimension] = ds.score;
      sum += ds.score;
      count++;
    }
    const average = count === 0 ? 0 : Math.round(sum / count);
    rows.push({
      department: dept,
      scores: scores as Record<ClimateDimension, number>,
      average,
    });
  }
  rows.sort((a, b) => b.average - a.average);
  return rows;
}

export interface ClimateInterpretation {
  label: string;
  color: string;
  description: string;
}

/**
 * Interpretación del Índice de Clima (0-100).
 */
export function getClimateInterpretation(score: number): ClimateInterpretation {
  if (score < 60) {
    return {
      label: 'Crítico / Riesgo',
      color: 'text-red-600 dark:text-red-400',
      description: 'Ambiente con desafíos severos. Requiere intervención inmediata.',
    };
  }
  if (score < 80) {
    return {
      label: 'Regular / Estable',
      color: 'text-yellow-600 dark:text-yellow-400',
      description: 'Clima funcional, pero con áreas de oportunidad claras.',
    };
  }
  return {
    label: 'Excelente',
    color: 'text-green-600 dark:text-green-400',
    description: 'Cultura de alto rendimiento y gran bienestar.',
  };
}

/**
 * Interpretación del eNPS (-100 a +100).
 */
export function getEnpsInterpretation(score: number): ClimateInterpretation {
  if (score < 0) {
    return {
      label: 'Zona Crítica',
      color: 'text-red-600 dark:text-red-400',
      description: 'Más detractores que promotores. Riesgo de rotación.',
    };
  }
  if (score < 30) {
    return {
      label: 'Bueno',
      color: 'text-yellow-600 dark:text-yellow-400',
      description: 'Mayoría satisfecha, nivel estándar de mercado.',
    };
  }
  if (score < 50) {
    return {
      label: 'Muy Bueno',
      color: 'text-green-500 dark:text-green-400',
      description: 'Alta lealtad y compromiso.',
    };
  }
  return {
    label: 'Clase Mundial',
    color: 'text-green-700 dark:text-green-500',
    description: 'Empleados embajadores de marca.',
  };
}

/**
 * Extrae comentarios abiertos de las respuestas.
 */
export function getOpenComments(responses: ClimateResponse[]): { department?: string; text: string }[] {
  const comments: { department?: string; text: string }[] = [];
  for (const r of responses) {
    const text = r.answers.open_comments;
    if (typeof text === 'string' && text.trim()) {
      comments.push({ department: r.department, text: text.trim() });
    }
  }
  return comments;
}
